import ky from "ky";
import { createListenerMiddleware } from "@reduxjs/toolkit";
import { initStyle } from "../reducers/historyEnhancer.js";
import { addSourceMetadata, addSourceTileJSON } from "../reducers/sourcesReducer.js";
import { setExtraSpec } from "../utils/actions.js";
import { compact, uniqBy } from "es-toolkit";
import { assistantChange, changeChatId } from "../reducers/chatReducer.js";
const listenerMiddleware = createListenerMiddleware();
const isMapkaSource = (host) => host.includes("mapka.localhost") || host.includes("mapka.dev");
/**
 * Split url into url and api key (take first one)
 * If this is mapka key use it as fallback
 * If not mapka only use url api key (this ensure that we do not leak api key)
 */
export const normalizeUrlSourceKey = (url = "", mapkaApiKey) => {
    const parsedUrl = new URL(url);
    const searchParams = new URLSearchParams(parsedUrl.search);
    const apiKey = searchParams.get("apiKey");
    const isMapka = isMapkaSource(url);
    if (!isMapka) {
        return [url, apiKey];
    }
    else {
        return [url, apiKey ?? mapkaApiKey];
    }
};
/**
 * Split tiles urls into url and api key (take first one)
 * If this is mapka key use it as fallback
 * If not mapka only use url api key (this ensure that we do not leak api key)
 */
export const normalizeTilesSourceKey = ([tile] = [], mapkaApiKey) => {
    const parsedUrl = new URL(tile);
    const searchParams = new URLSearchParams(parsedUrl.search);
    const apiKey = searchParams.get("apiKey");
    const tileJsonUrl = tile
        .replace("{z}/{x}/{y}.png", "tile.json")
        .replace("{z}/{x}/{y}.pbf", "tile.json")
        .replace("{z}/{x}/{y}.jpg", "tile.json");
    const isMapka = isMapkaSource(tile);
    if (!isMapka) {
        return [tileJsonUrl, apiKey];
    }
    else {
        return [tileJsonUrl, apiKey ?? mapkaApiKey];
    }
};
function getUniqueSourcesUrl(sources, apiKey) {
    const allSourcesUrl = sources
        .filter(source => source.type === "vector" || source.type === "raster" || source.type === "geojson")
        .map((source) => {
        if ("data" in source && typeof source.data === "string") {
            return normalizeUrlSourceKey(source.data, apiKey);
        }
        if ("url" in source && typeof source.url === "string") {
            return normalizeUrlSourceKey(source.url, apiKey);
        }
        if ("tiles" in source && typeof source.tiles === "object") {
            return normalizeTilesSourceKey(source.tiles, apiKey);
        }
        return null;
    })
        .filter(sourceUrl => sourceUrl);
    return uniqBy(compact(allSourcesUrl), ([sourceUrl]) => sourceUrl);
}
/**
 * Rewrite a Mapka tileset endpoint URL into its metadata endpoint.
 * Handles both `/tile.json` (vector/raster) and `/data.geojson(l)?` (geojson)
 * terminal segments. The server's /metadata endpoint returns the stored
 * `version.metadata` regardless of format.
 */
const toMetadataURL = (url) => url
    .replace("tile.json", "metadata")
    .replace(/data\.geojsonl?/, "metadata");
const fetchSourceMetadata = async (urls, state, dispatch) => {
    for (const [url, apiKey] of urls) {
        if (!apiKey) {
            continue;
        }
        if (url in state.present.sources.tilejson || url in state.present.sources.metadata) {
            continue;
        }
        const { host } = new URL(url);
        const isMapka = isMapkaSource(host);
        if (isMapka) {
            const metadataURL = toMetadataURL(url);
            try {
                const metadata = await ky.get(metadataURL, {
                    headers: {
                        authorization: `Bearer ${apiKey}`,
                    },
                }).json();
                dispatch(addSourceMetadata({
                    url,
                    metadata,
                }));
            }
            catch (error) {
                window.logger.error({ url, error }, "Failed to fetch source tilejson");
            }
        }
        else {
            try {
                const tilejson = await ky.get(url, {
                    headers: {
                        authorization: `Bearer ${apiKey}`,
                    },
                }).json();
                dispatch(addSourceTileJSON({
                    url,
                    tilejson,
                }));
            }
            catch (error) {
                window.logger.warn({ url, error }, "Failed to fetch source tilejson");
            }
        }
    }
};
listenerMiddleware.startListening({
    actionCreator: initStyle,
    effect: async (action, listenerApi) => {
        listenerApi.cancelActiveListeners();
        const state = listenerApi.getState();
        const apiKey = state.present.apiKey.mapkaAPIKey;
        const allSources = action.payload.versions.flatMap(({ spec }) => Object.values(spec.sources));
        const allUniqueSourcesUrl = getUniqueSourcesUrl(allSources, apiKey);
        await fetchSourceMetadata(allUniqueSourcesUrl, state, listenerApi.dispatch);
    },
});
listenerMiddleware.startListening({
    actionCreator: setExtraSpec,
    effect: async (action, listenerApi) => {
        listenerApi.cancelActiveListeners();
        const state = listenerApi.getState();
        const apiKey = state.present.apiKey.mapkaAPIKey;
        const sources = action.payload.sources ?? {};
        const allSources = Object.values(sources);
        const allUniqueSourcesUrl = getUniqueSourcesUrl(allSources, apiKey);
        await fetchSourceMetadata(allUniqueSourcesUrl, state, listenerApi.dispatch);
    },
});
listenerMiddleware.startListening({
    actionCreator: assistantChange,
    effect: async (action, listenerApi) => {
        listenerApi.cancelActiveListeners();
        const state = listenerApi.getState();
        const apiKey = state.present.apiKey.mapkaAPIKey;
        const { sources } = action.payload.entry.spec;
        const allSources = Object.values(sources);
        const allUniqueSourcesUrl = getUniqueSourcesUrl(allSources, apiKey);
        await fetchSourceMetadata(allUniqueSourcesUrl, state, listenerApi.dispatch);
    },
});
listenerMiddleware.startListening({
    actionCreator: changeChatId,
    effect: async (action, listenerApi) => {
        listenerApi.cancelActiveListeners();
        const state = listenerApi.getState();
        const apiKey = state.present.apiKey.mapkaAPIKey;
        const allSources = action.payload.history.flatMap(({ spec }) => Object.values(spec.sources));
        const allUniqueSourcesUrl = getUniqueSourcesUrl(allSources, apiKey);
        await fetchSourceMetadata(allUniqueSourcesUrl, state, listenerApi.dispatch);
    },
});
export const sourcesMiddleware = listenerMiddleware.middleware;
